

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts

import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import RTL from "layouts/rtl";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";

import UserProfile from "layouts/user-profile";
import UserManagement from "layouts/user-management";

import PlatformScout from "layouts/platform-scout";
import SocialStash from "layouts/social-stash";
import SocialStudio from "layouts/social-studio";
import InsightHub from "layouts/insight-hub";
import ConnectProfileResponseRouter
  from "layouts/components/ConnectOwnProfile/response/ResponseRouter";


import Demo from "auth/demo";
import LiveDemo from "auth/live-demo";
import Login from "auth/login";
import Register from "auth/register";
import ForgotPassword from "auth/forgot-password";
import ResetPassword from "auth/reset-password";

import OAuthCallback from "auth/oauth/OAuthCallback";
import FederatedSuccess from "auth/oauth";

import PaymentSuccess from "payment/success";
import AddonPaymentSuccess from "payment/addon-success";
import AddonPaymentFail from "payment/addon-fail";
import PaymentFail from "payment/fail";


import { StableNavigateContextProvider } from "context/StableNavigateContext";

// @mui icons
import Icon from "@mui/material/Icon";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Platform Scout", // Name shown in the sidebar
    key: "platform-scout", // Unique key for the route
    icon: <Icon fontSize="small">explore</Icon>, // Icon for the route
    route: "/platform-scout", // Route path
    component: <PlatformScout />, // Corresponding component
  },
  {
    type: "collapse",
    name: "Social Stash",
    key: "social-stash",
    icon: <Icon fontSize="small">inventory_2</Icon>,
    route: "/social-stash",
    component: <SocialStash />,
  },
  {
    type: "collapse",
    name: "Insight Hub",
    key: "insight-hub",
    icon: <Icon fontSize="small">lightbulb_outline</Icon>,
    route: "/insight-hub",
    component: <StableNavigateContextProvider><InsightHub /></StableNavigateContextProvider>,
  },
  {
    type: "collapse",
    name: "Social Studio",
    key: "social-studio",
    icon: <Icon fontSize="small">insights</Icon>,
    route: "/social-studio",
    component: <SocialStudio />,
  },
  // {
  //   type: "collapse",
  //   name: "Billing",
  //   key: "billing",
  //   icon: <Icon fontSize="small">receipt_long</Icon>,
  //   route: "/billing",
  //   component: <Billing />,
  // },
  {
    type: "route", // Changed from "collapse" to hide from Sidenav
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
  },
  {
    type: "auth",
    name: "Login",
    key: "login",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/auth/login",
    component: <Login />,
  },
  {
    type: "auth",
    name: "Register",
    key: "register",
    icon: <Icon fontSize="small">reigster</Icon>,
    route: "/auth/register",
    component: <Register />,
  },
  {
    type: "auth",
    name: "Forgot Password",
    key: "forgot-password",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/auth/forgot-password",
    component: <ForgotPassword />,
  },
  {
    type: "auth",
    name: "Reset Password",
    key: "reset-password",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/auth/reset-password",
    component: <ResetPassword />,
  },
  {
    type: "auth",
    name: "Request a Demo",
    key: "demo",
    icon: <Icon fontSize="small">event_note</Icon>,
    route: "/auth/demo",
    component: <Demo />,
  },
  {
    type: "auth",
    name: "Live Demo",
    key: "live-demo",
    icon: <Icon fontSize="small">play_circle</Icon>,
    route: "/auth/live-demo",
    component: <LiveDemo />,
  },

  {
    type: "auth",
    name: "OAuth Callback",
    key: "oauth-callback",
    route: "/auth/oauth/:provider/callback",
    component: <OAuthCallback />,
  },
  {
    type: "auth",
    name: "Federated Success",
    key: "federated-success",
    route: "/auth/federated-success",
    component: <FederatedSuccess />,
  },
  {
    type: "route",
    name: "Payment Success",
    key: "payment-success",
    route: "/payment/success",
    component: <PaymentSuccess />,
  },
  {
    type: "route",
    name: "Payment Fail",
    key: "payment-fail",
    route: "/payment/fail",
    component: <PaymentFail />,
  },
  {
    type: "route",
    name: "Addon Payment Success",
    key: "addon-payment-success",
    route: "/payment/addon-success",
    component: <AddonPaymentSuccess />,
  },
  {
    type: "route",
    name: "Addon Payment Fail",
    key: "addon-payment-fail",
    route: "/payment/addon-fail",
    component: <AddonPaymentFail />,
  },
  {
    type: "route",
    name: "Connect Own Profile Response",
    key: "connect-own-profile-response",
    route: "/connect/profile/response/:platform?/:status?",
    component: <ConnectProfileResponseRouter />,
  },
  // [NEW] Shortened Routes for YouTube flow
  {
    type: "route",
    name: "YouTube Success",
    key: "youtube-success",
    route: "/Youtube-Success",
    component: <ConnectProfileResponseRouter platform="youtube" status="success" />,
  },
  {
    type: "route",
    name: "YouTube No Channel",
    key: "youtube-no-channel",
    route: "/Youtube-NoChannel",
    component: <ConnectProfileResponseRouter platform="youtube" status="no-channel" />,
  },
  {
    type: "route",
    name: "YouTube Cancelled",
    key: "youtube-cancelled",
    route: "/Youtube-Cancelled",
    component: <ConnectProfileResponseRouter platform="youtube" status="cancelled" />,
  },

];

export default routes;
